import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { AITier, AIFeature } from '../../types/admin';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';
import { Button } from '../common/Button';

/**
 * AI Configuration Tab
 * Implements Requirement 82 AC 3: AI configuration
 * Provides tier enablement, kill switch, per-feature consent, cost caps, model preferences
 */
export const AIConfigTab: React.FC = () => {
  const [showKillSwitchConfirm, setShowKillSwitchConfirm] = useState(false);
  const [showL1Confirm, setShowL1Confirm] = useState(false);
  const [l1Updating, setL1Updating] = useState(false);
  const [l1Policy, setL1Policy] = useState<string | null>(null);

  const {
    data: aiConfig,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['aiConfig'],
    queryFn: () => adminService.getAIConfig(),
  });

  const {
    data: l1Config,
    refetch: refetchL1Config,
  } = useQuery({
    queryKey: ['l1ExecutionConfig'],
    queryFn: () => adminService.getL1ExecutionConfig(),
  });

  const handleToggleL1Execution = async () => {
    setL1Updating(true);
    try {
      const result = await adminService.updateL1ExecutionConfig(!l1Config?.enabled);
      setL1Policy(result.additionalPolicy);
      setShowL1Confirm(false);
      refetchL1Config();
    } catch (error) {
      alert('Failed to toggle L1 real execution');
    } finally {
      setL1Updating(false);
    }
  };

  const tierLabels: Record<AITier, string> = {
    [AITier.T_ML]: 'T-ML (Platform Native)',
    [AITier.T1]: 'T1 (Frontier LLM)',
    [AITier.T2]: 'T2 (Cost-Optimized LLM)',
    [AITier.T3]: 'T3 (Offline/Templated)',
  };

  const featureLabels: Record<AIFeature, string> = {
    [AIFeature.ANOMALY_EXPLANATION]: 'Anomaly Explanation',
    [AIFeature.RCA_HYPOTHESIS]: 'RCA Hypothesis',
    [AIFeature.RECOMMENDATION_EXPLANATION]: 'Recommendation Explanation',
    [AIFeature.OWNERSHIP_HYPOTHESIS]: 'Ownership Hypothesis',
    [AIFeature.COPILOT]: 'FinOps Copilot',
    [AIFeature.NL_COST_EXPLORER]: 'NL Cost Explorer',
  };

  const handleToggleKillSwitch = async () => {
    if (!aiConfig) return;

    try {
      await adminService.updateAIConfig({
        killSwitchEnabled: !aiConfig.killSwitchEnabled,
      });
      alert(`AI Switch ${!aiConfig.killSwitchEnabled ? 'ENABLED' : 'DISABLED'}`);
      setShowKillSwitchConfirm(false);
      refetch();
    } catch (error) {
      alert('Failed to toggle kill switch');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getSpendPercentage = () => {
    if (!aiConfig || !aiConfig.totalMonthlyCostCap) return 0;
    return (aiConfig.currentMonthlySpend / aiConfig.totalMonthlyCostCap) * 100;
  };

  const getSpendColor = () => {
    const percentage = getSpendPercentage();
    if (percentage >= 90) return 'bg-rose-500';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  if (isLoading) {
    return <LoadingState message="Loading AI configuration..." />;
  }

  if (error || !aiConfig) {
    return (
      <ErrorState
        title="Failed to load AI configuration"
        message="Unable to fetch AI configuration data. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-sm font-semibold text-foreground">AI Configuration</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage AI tier enablement, cost controls, and feature consent
        </p>
      </div>

      {/* AI Switch */}
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 dark:border-rose-800 dark:bg-rose-900/20">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-rose-900 dark:text-rose-200">AI Switch</h3>
            <p className="mt-1 text-sm text-rose-700 dark:text-rose-300">
              Emergency control to disable all AI features globally. Takes effect in &lt; 60 seconds.
            </p>
          </div>
          <Button
            variant={aiConfig.killSwitchEnabled ? 'success' : 'danger'}
            size="sm"
            className="shrink-0"
            onClick={() => setShowKillSwitchConfirm(true)}
          >
            {aiConfig.killSwitchEnabled ? 'Re-enable AI' : 'Disable All AI'}
          </Button>
        </div>
        {aiConfig.killSwitchEnabled && (
          <div className="mt-3 rounded-md bg-rose-100 p-2.5 dark:bg-rose-900/40">
            <p className="text-sm font-medium text-rose-900 dark:text-rose-200">
              ⚠️ AI Switch is set to OFF. All AI features are currently disabled.
            </p>
          </div>
        )}
      </div>

      {/* L1 Real Execution - Requirement 49 AC3/AC4, a SEPARATE kill switch
          from the AI switch above: that one gates LLM calls, this one
          gates real cloud mutation (l1-mutation.service.ts). Defaults off. */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">L1 Real Execution</h3>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              When on, L1-classified cost anomalies are automatically stopped/resized/deleted for real (AWS
              only today) instead of just generating a script for a human to run. Every action still passes a
              live non-prod tag check, a daily blast-radius cap, and requires a separate AWS write-permission
              grant beyond onboarding's read-only role.
            </p>
          </div>
          <Button
            variant={l1Config?.enabled ? 'danger' : 'outline'}
            size="sm"
            className="shrink-0"
            disabled={!l1Config}
            onClick={() => setShowL1Confirm(true)}
          >
            {l1Config?.enabled ? 'Disable Real Execution' : 'Enable Real Execution'}
          </Button>
        </div>
        {l1Config?.enabled && (
          <div className="mt-3 rounded-md bg-amber-100 p-2.5 dark:bg-amber-900/40">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              L1 real execution is ACTIVE for this tenant.
            </p>
          </div>
        )}
        {l1Policy && (
          <div className="mt-3 rounded-md border border-amber-300 bg-card p-3 dark:border-amber-700 dark:bg-card">
            <p className="text-sm font-medium text-foreground">
              Apply this additional IAM policy in your AWS account - real execution will fail with AccessDenied until you do:
            </p>
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted/30 p-3 text-xs text-foreground dark:bg-card dark:text-foreground">
{l1Policy}
            </pre>
          </div>
        )}
      </div>

      {/* Cost Monitoring */}
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="text-sm font-semibold text-foreground">Monthly Cost Monitoring</h3>
        <div className="mt-4 space-y-4">
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Current Month Spend</span>
              <span className="font-medium text-foreground">
                {formatCurrency(aiConfig.currentMonthlySpend)} /{' '}
                {aiConfig.totalMonthlyCostCap
                  ? formatCurrency(aiConfig.totalMonthlyCostCap)
                  : 'No Cap'}
              </span>
            </div>
            {aiConfig.totalMonthlyCostCap && (
              <div className="mt-2">
                <div className="h-2 w-full rounded-full bg-secondary">
                  <div
                    className={`h-2 rounded-full ${getSpendColor()}`}
                    style={{ width: `${Math.min(getSpendPercentage(), 100)}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {getSpendPercentage().toFixed(1)}% of monthly cap
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80">
              Total Monthly Cost Cap (USD)
            </label>
            <input
              type="number"
              value={aiConfig.totalMonthlyCostCap || ''}
              onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value, 10) : null;
                adminService.updateAIConfig({ totalMonthlyCostCap: value });
                refetch();
              }}
              placeholder="No cap"
              className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* AI Tiers */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">AI Tier Configuration</h3>
        {aiConfig.tiers.map((tierConfig: any) => (
          <div
            key={tierConfig.tier}
            className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card"
          >
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-medium text-foreground">
                  {tierLabels[tierConfig.tier as AITier]}
                </h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  {tierConfig.tier === AITier.T_ML &&
                    'Platform-native lightweight ML (embeddings, classifiers, NER)'}
                  {tierConfig.tier === AITier.T1 &&
                    'Frontier LLM for complex reasoning (Claude, GPT-4)'}
                  {tierConfig.tier === AITier.T2 &&
                    'Cost-optimized LLM for standard tasks (GPT-4o-mini)'}
                  {tierConfig.tier === AITier.T3 && 'Offline/templated narratives (no external LLM)'}
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={tierConfig.enabled}
                  onChange={() => {
                    const updatedTiers = aiConfig.tiers.map((t: any) =>
                      t.tier === tierConfig.tier ? { ...t, enabled: !t.enabled } : t
                    );
                    adminService.updateAIConfig({ tiers: updatedTiers });
                    refetch();
                  }}
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-secondary after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-border after:bg-card after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring dark:border-border dark:bg-secondary dark:peer-focus:ring-ring"></div>
              </label>
            </div>

            {tierConfig.enabled && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground/80">
                    Features
                  </label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tierConfig.features.map((feature: any) => (
                      <span
                        key={feature}
                        className="inline-flex rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary dark:bg-primary/10 dark:text-primary"
                      >
                        {featureLabels[feature as AIFeature]}
                      </span>
                    ))}
                  </div>
                </div>

                {tierConfig.costCap !== null && (
                  <div>
                    <label className="block text-sm font-medium text-foreground/80">
                      Monthly Cost Cap (USD)
                    </label>
                    <input
                      type="number"
                      value={tierConfig.costCap}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value, 10) : null;
                        const updatedTiers = aiConfig.tiers.map((t: any) =>
                          t.tier === tierConfig.tier ? { ...t, costCap: value } : t
                        );
                        adminService.updateAIConfig({ tiers: updatedTiers });
                        refetch();
                      }}
                      className="mt-1 block w-full rounded-md border border-border px-3 py-2 shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Per-Feature Consent */}
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
        <h3 className="text-sm font-semibold text-foreground">Per-Feature Consent</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Control which AI features are allowed to process your data
        </p>
        <div className="mt-4 space-y-3">
          {Object.entries(aiConfig.perFeatureConsent).map(([feature, enabled]) => (
            <label key={feature} className="flex items-center justify-between">
              <span className="text-sm text-foreground/80">
                {featureLabels[feature as AIFeature]}
              </span>
              <input
                type="checkbox"
                checked={enabled as boolean}
                onChange={() => {
                  const updatedConsent = { ...aiConfig.perFeatureConsent, [feature]: !enabled };
                  adminService.updateAIConfig({ perFeatureConsent: updatedConsent });
                  refetch();
                }}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring dark:border-border dark:bg-secondary"
              />
            </label>
          ))}
        </div>
      </div>

      {/* Kill Switch Confirmation Modal */}
      {showKillSwitchConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/20">
                <svg className="h-6 w-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {aiConfig.killSwitchEnabled ? 'Re-enable AI Features?' : 'Disable All AI Features?'}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {aiConfig.killSwitchEnabled
                    ? 'This will re-enable all AI features according to their individual settings.'
                    : 'This will immediately disable all AI features across the platform. This action takes effect in less than 60 seconds.'}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowKillSwitchConfirm(false)}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleKillSwitch}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  aiConfig.killSwitchEnabled
                    ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500'
                    : 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-500'
                }`}
              >
                {aiConfig.killSwitchEnabled ? 'Re-enable AI' : 'Disable All AI'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* L1 Real Execution Confirmation Modal */}
      {showL1Confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-card p-4 shadow-xl dark:bg-card">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
                <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {l1Config?.enabled ? 'Disable L1 Real Execution?' : 'Enable L1 Real Execution?'}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {l1Config?.enabled
                    ? 'L1-classified anomalies will go back to generating a script for a human to run, instead of executing automatically.'
                    : 'L1-classified anomalies will be automatically stopped/resized/deleted for real. You will need to apply an additional AWS IAM policy (shown after confirming) before anything can actually run.'}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowL1Confirm(false)}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleL1Execution}
                disabled={l1Updating}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                  l1Config?.enabled ? 'bg-secondary hover:bg-secondary focus:ring-ring' : 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-500'
                }`}
              >
                {l1Updating ? 'Saving…' : l1Config?.enabled ? 'Disable Real Execution' : 'Enable Real Execution'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
